import FilterPopover, { FilterChoice } from "@/components/common/FilterPopover";
import PayRange from "@/components/common/PayRange";
import { LABEL_CLASS } from "@/components/common/controls";
import type { SearchCounts } from "@/lib/api/catalogue";
import {
    languagesSummary,
    moreLanguages,
    paySummary,
    POSTED_OPTION,
    postedSummary,
    QUICK_LANGUAGES,
    settingsSummary,
    toggledSetting,
    type JobsView,
} from "@/lib/jobs";
import { languageName, toggledLanguage } from "@/lib/languages";
import { POSTED_WINDOWS, settingCount, WORK_SETTING_LABEL, WORK_SETTINGS } from "@/lib/openings";
import FilterCount from "./FilterCount";

/** Whether a filter's panel is the one open. */
export interface FilterToggle {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface FilterProps {
    view: JobsView;
    toggle: FilterToggle;
    onChange: (patch: Partial<JobsView>) => void;
}

const ANY_TIME = "any";
const LIST_CLASS = "flex flex-col gap-0.5 p-1.5";

/** How a posting is worked: any of them, or none picked for all of them. */
export function WorkSettingFilter({
    view,
    counts,
    toggle,
    onChange,
}: FilterProps & { counts: SearchCounts | undefined }) {
    return (
        <FilterPopover
            {...toggle}
            label="Work arrangement"
            summary={settingsSummary(view.settings)}
            analyticsField="work_setting"
        >
            {() => (
                <div role="group" aria-label="Work arrangement" className={LIST_CLASS}>
                    {WORK_SETTINGS.map((setting) => {
                        const selected = view.settings.includes(setting);
                        return (
                            <FilterChoice
                                key={setting}
                                type="checkbox"
                                name="work_setting"
                                checked={selected}
                                action="job_work_setting_toggled"
                                context={{
                                    source: "Jobs",
                                    option: setting,
                                    next_state: !selected,
                                }}
                                onChange={() =>
                                    onChange({ settings: toggledSetting(view.settings, setting) })
                                }
                                count={
                                    <FilterCount
                                        value={settingCount(counts, setting)}
                                        tone="text-fg-faint"
                                    />
                                }
                            >
                                {WORK_SETTING_LABEL[setting]}
                            </FilterChoice>
                        );
                    })}
                </div>
            )}
        </FilterPopover>
    );
}

/** How recently a posting went up. */
export function PostedFilter({ view, toggle, onChange }: FilterProps) {
    return (
        <FilterPopover
            {...toggle}
            label="Posted"
            summary={postedSummary(view.posted)}
            analyticsField="posted"
        >
            {() => (
                <div role="radiogroup" aria-label="Posted" className={LIST_CLASS}>
                    <FilterChoice
                        type="radio"
                        name="posted"
                        checked={view.posted === null}
                        action="job_posted_window_selected"
                        context={{ source: "Jobs", option: ANY_TIME }}
                        onChange={() => onChange({ posted: null })}
                    >
                        Any time
                    </FilterChoice>
                    {POSTED_WINDOWS.map((window) => (
                        <FilterChoice
                            key={window}
                            type="radio"
                            name="posted"
                            checked={view.posted === window}
                            action="job_posted_window_selected"
                            context={{ source: "Jobs", option: window }}
                            onChange={() => onChange({ posted: window })}
                        >
                            {POSTED_OPTION[window]}
                        </FilterChoice>
                    ))}
                </div>
            )}
        </FilterPopover>
    );
}

/** The yearly salary range. The only filter held back until Apply. */
export function SalaryFilter({ view, toggle, onChange }: FilterProps) {
    return (
        <FilterPopover
            {...toggle}
            wide
            label="Salary"
            summary={paySummary(view.payAtLeast, view.payAtMost)}
            analyticsField="salary"
        >
            {(close) => (
                <PayRange
                    // The back button can change the range under an open panel.
                    key={`${view.payAtLeast}:${view.payAtMost}:${view.keepUnstatedPay}`}
                    minimum={view.payAtLeast}
                    maximum={view.payAtMost}
                    keepUnstated={view.keepUnstatedPay}
                    onApply={(payAtLeast, payAtMost, keepUnstatedPay) => {
                        onChange({ payAtLeast, payAtMost, keepUnstatedPay });
                        close();
                    }}
                />
            )}
        </FilterPopover>
    );
}

/** The languages a posting may be written in, any number of them. */
export function LanguageFilter({ view, toggle, onChange }: FilterProps) {
    const choice = (code: string, name: string) => {
        const picked = view.languages.includes(code);
        return (
            <FilterChoice
                key={code}
                type="checkbox"
                name="language"
                checked={picked}
                action="filter_option_selected"
                context={{ source: "Jobs", field: "language", option: code }}
                onChange={() => onChange({ languages: toggledLanguage(view.languages, code) })}
            >
                {name}
            </FilterChoice>
        );
    };
    return (
        <FilterPopover
            {...toggle}
            label="Language"
            summary={languagesSummary(view.languages)}
            analyticsField="language"
        >
            {() => (
                <div role="group" aria-label="Language" className={LIST_CLASS}>
                    <FilterChoice
                        type="checkbox"
                        name="language"
                        checked={view.languages.length === 0}
                        action="filter_option_selected"
                        context={{ source: "Jobs", field: "language", option: "any" }}
                        onChange={() => onChange({ languages: [] })}
                    >
                        Any language
                    </FilterChoice>
                    {QUICK_LANGUAGES.map(([code, name]) => choice(code, name))}
                    <span className={`${LABEL_CLASS} px-2.5 pt-2 pb-1`}>More languages</span>
                    {moreLanguages(view.languages).map((code) => choice(code, languageName(code)))}
                </div>
            )}
        </FilterPopover>
    );
}
